"use client";

import { useState, useEffect, useRef } from "react";
import { getDb } from "@/lib/db";
import type { AiProfile } from "@/types/ai";
import type { Team, TeamMember } from "@/types/collaboration";
import { Hash, Users, Plus, Send, AtSign, Lock } from "lucide-react";
import { useUser } from "@/lib/user/context";
import { getPlanFeatures } from "@/lib/subscription/plans";
import type { SubscriptionPlan } from "@/lib/subscription/plans";

interface TeamMessage {
  id: string;
  teamId: string;
  senderId: string;
  senderType: "human" | "ai";
  senderName: string;
  senderRole?: string; // For AIs: their role (e.g., "Copywriter", "Analyst")
  channelId: string;
  content: string;
  mentions?: string[]; // Array of @mentioned user/AI IDs
  createdAt: number;
  isAIResponse?: boolean; // True if AI responded automatically
}

interface Channel {
  id: string;
  teamId: string;
  name: string;
  description?: string;
  isPrivate: boolean;
  createdAt: number;
}

interface Props {
  team: Team;
  currentUserId: string;
  userAIs: AiProfile[]; // Current user's AIs
  teamMemberAIs: AiProfile[]; // Other team members' AIs
  teamMembers: TeamMember[]; // Human team members
}

/**
 * Team Workspace Chat
 * 
 * Unified workspace where humans and AIs collaborate together.
 * Features:
 * - Channels (like Slack)
 * - @mention system (AIs only respond when @mentioned to save tokens)
 * - Role-based AI participation
 * - Real-time messaging
 */
export default function TeamWorkspace({
  team,
  currentUserId,
  userAIs,
  teamMemberAIs,
  teamMembers,
}: Props) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const [messages, setMessages] = useState<TeamMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { user } = useUser();
  const tier = user?.subscriptionTier;
  const plan: SubscriptionPlan =
    tier === "pro" || tier === "pro_plus" || tier === "enterprise"
      ? tier
      : tier === "personal"
        ? "pro"
        : tier === "business"
          ? "pro_plus"
          : "free";
  const planFeatures = getPlanFeatures(plan);
  const hasTeamAccess = planFeatures.teamCollaboration;

  useEffect(() => {
    loadChannels();
  }, [team.id]);

  useEffect(() => {
    if (activeChannel) {
      loadMessages(activeChannel);
    }
  }, [activeChannel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function loadChannels() {
    try {
      const db = getDb();
      // Create default channel if none exist
      const existingChannels = await db.channels
        ?.where("teamId")
        .equals(team.id)
        .toArray() || [];

      if (existingChannels.length === 0) {
        const defaultChannel: Channel = {
          id: crypto.randomUUID(),
          teamId: team.id,
          name: "general",
          description: "General team discussion",
          isPrivate: false,
          createdAt: Date.now(),
        };
        await db.channels.add(defaultChannel);
        setChannels([defaultChannel]);
        setActiveChannel(defaultChannel.id);
      } else {
        setChannels(existingChannels);
        if (!activeChannel) {
          setActiveChannel(existingChannels[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to load channels:", error);
    }
  }

  async function loadMessages(channelId: string) {
    try {
      const db = getDb();
      const channelMessages = await db.teamMessages
        ?.where("channelId")
        .equals(channelId)
        .sortBy("createdAt") || [];
      setMessages(channelMessages);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  }

  async function handleSend() {
    if (!input.trim() || !activeChannel || loading) return;

    const db = getDb();
    const messageContent = input.trim();
    setInput("");
    setLoading(true);

    // Parse @mentions
    const mentionRegex = /@(\w+)/g;
    const mentions: string[] = [];
    let match;
    while ((match = mentionRegex.exec(messageContent)) !== null) {
      mentions.push(match[1]);
    }

    // Create message
    const message: TeamMessage = {
      id: crypto.randomUUID(),
      teamId: team.id,
      senderId: currentUserId,
      senderType: "human",
      senderName: "You", // TODO: Get actual user name
      channelId: activeChannel,
      content: messageContent,
      mentions,
      createdAt: Date.now(),
    };

    // Save message
    try {
      await db.teamMessages?.add(message);
      setMessages((prev) => [...prev, message]);
    } catch (error) {
      console.error("Failed to save message:", error);
    }

    // Check if any AIs should respond
    const shouldRespond = mentions.length > 0 || messageContent.toLowerCase().includes("help") || messageContent.toLowerCase().includes("?");

    if (shouldRespond) {
      // Find mentioned AIs or relevant AIs
      const mentionedAIs = mentions.length > 0
        ? [...userAIs, ...teamMemberAIs].filter(ai =>
            mentions.some(mention => 
              ai.name.toLowerCase().includes(mention.toLowerCase()) ||
              ai.id.toLowerCase().includes(mention.toLowerCase())
            )
          )
        : []; // Only respond if @mentioned (to save tokens)

      // For now, just show that AIs would respond
      // In production, this would trigger AI responses
      if (mentionedAIs.length > 0) {
        // Simulate AI response (in production, call AI API)
        setTimeout(() => {
          const aiResponse: TeamMessage = {
            id: crypto.randomUUID(),
            teamId: team.id,
            senderId: mentionedAIs[0].id,
            senderType: "ai",
            senderName: mentionedAIs[0].name,
            senderRole: "Assistant", // TODO: Get from AI profile
            channelId: activeChannel,
            content: `I'll help with that. (This is a placeholder - AI response would be generated here)`,
            createdAt: Date.now(),
            isAIResponse: true,
          };
          setMessages((prev) => [...prev, aiResponse]);
          setLoading(false);
        }, 1000);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }

  // Get all participants (humans + AIs)
  const allParticipants = [
    ...teamMembers.map(m => ({ id: m.userId, name: m.email, type: "human" as const })),
    ...userAIs.map(ai => ({ id: ai.id, name: ai.name, type: "ai" as const, role: "Assistant" })),
    ...teamMemberAIs.map(ai => ({ id: ai.id, name: ai.name, type: "ai" as const, role: "Assistant" })),
  ];

  if (!hasTeamAccess) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Team Collaboration Requires Pro Plus
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Upgrade to Pro Plus to unlock team workspaces with humans and AIs.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900">
      {/* Sidebar - Channels */}
      <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {team.name}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          <div className="mb-2">
            <button className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
              <Plus className="w-4 h-4" />
              <span className="text-sm">Create Channel</span>
            </button>
          </div>

          <div className="space-y-1">
            {channels.map((channel) => (
              <button
                key={channel.id}
                onClick={() => setActiveChannel(channel.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                  activeChannel === channel.id
                    ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                }`}
              >
                <Hash className="w-4 h-4" />
                <span className="text-sm font-medium">{channel.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Participants */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-500">Participants</span>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {allParticipants.slice(0, 10).map((participant) => (
              <div
                key={participant.id}
                className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2"
              >
                <div className={`w-2 h-2 rounded-full ${
                  participant.type === "ai" ? "bg-purple-500" : "bg-green-500"
                }`} />
                <span>{participant.name}</span>
                {participant.type === "ai" && participant.role && (
                  <span className="text-gray-400">({participant.role})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {activeChannel ? (
          <>
            {/* Channel Header */}
            <div className="h-16 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center px-6">
              <Hash className="w-5 h-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {channels.find(c => c.id === activeChannel)?.name || "Channel"}
              </h3>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${
                    msg.senderType === "human" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-2xl rounded-lg px-4 py-2 ${
                      msg.senderType === "human"
                        ? "bg-blue-500 text-white"
                        : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">
                        {msg.senderName}
                      </span>
                      {msg.senderRole && (
                        <span className="text-xs opacity-70">
                          ({msg.senderRole})
                        </span>
                      )}
                      {msg.isAIResponse && (
                        <span className="text-xs opacity-70">
                          • AI Response
                        </span>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <span className="text-xs opacity-70 mt-1 block">
                      {new Date(msg.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <AtSign className="w-5 h-5 text-gray-400" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={`Message #${channels.find(c => c.id === activeChannel)?.name || "channel"}...`}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Tip: @mention AIs to get their help. AIs only respond when @mentioned to save tokens.
              </p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">Select a channel to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
}

